import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import PhotoInspector from "@/pages/PhotoInspector";
import CivilChat from "@/pages/CivilChat";
import DailyReports from "@/pages/DailyReports";
import Inventory from "@/pages/Inventory";
import BOQ from "@/pages/BOQ";
import DelayPrediction from "@/pages/DelayPrediction";
import Safety from "@/pages/Safety";
import Team from "@/pages/Team";
import EquipmentQR from "@/pages/EquipmentQR";
import Subscription from "@/pages/Subscription";
import Settings from "@/pages/Settings";
import VoiceLogs from "@/pages/VoiceLogs";
import WeatherScheduler from "@/pages/WeatherScheduler";
import Attendance from "@/pages/Attendance";
import WhatsAppReport from "@/pages/WhatsAppReport";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Features from "@/pages/Features";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Analytics from "@/components/Analytics";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback synchronously (before any protected route runs)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <Analytics />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/features" element={<Features />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/app" element={<Protected><AppLayout /></Protected>}>
          <Route index element={<Dashboard />} />
          <Route path="inspector" element={<PhotoInspector />} />
          <Route path="chat" element={<CivilChat />} />
          <Route path="reports" element={<DailyReports />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="boq" element={<BOQ />} />
          <Route path="delay" element={<DelayPrediction />} />
          <Route path="safety" element={<Safety />} />
          <Route path="team" element={<Team />} />
          <Route path="equipment" element={<EquipmentQR />} />
          <Route path="voice" element={<VoiceLogs />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="whatsapp" element={<WhatsAppReport />} />
          <Route path="weather" element={<WeatherScheduler />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
